// ----------------------------------------------------------------------
// <copyright file="Izenda.WorkspaceTenant.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

// Workspace Tenant model
!(function() {

  var m = require('mobx');
  var trackable = require('share/tracking/Izenda.Trackable');
  var CopyManagementUtils = require('share/utils/Izenda.CopyManagementUtils');
  var ObjectStateConstants = require('logic/data/Izenda.ObjectStateConstants');
  var QuerySourceTree = require('./Izenda.QuerySourceTreeMultiLevel');
  var WorkspaceMapping = require('logic/models/Izenda.WorkspaceMapping');
  var constants = require('logic/data/Izenda.CopyManagementConstants');
  const { map, has, forEach, find } = require('lodash/fp');
  
  function WorkspaceTenant(data, tenantMapping, databaseSources, sourceConnections) {
    var props = {
      tenantName: '',
      name: '',
      tenantUniqueName: null,
      workspaceId: null,
      tenantId: null,
      status: 0,
      state: ObjectStateConstants.NONE,
      inserted: false,
      version: null,
      created: null,
      createdBy: null,
      modified: null,
      modifiedBy: null,
      destinationHashCode: null,
      dataMapping: [], // Advanced Copy Mapping Grid
      databaseSources: [], // Connection Detail\
      sourceConnections: sourceConnections,
      destinationConnections: [],
      querySourceTree: new QuerySourceTree([], null, true),
      destinationQuerySourceTree: new QuerySourceTree([], null, true)
    };

    m.extendObservable(trackable(this), props);

    data && this.poppulateData(data, tenantMapping, databaseSources, sourceConnections);
    this.resetDirty();
  }

  /**
   * Populate Workspace tenant data
   * @param {type} data
   */
  WorkspaceTenant.prototype.poppulateData = m.action(function(data, tenantMapping, databaseSources, sourceConnections) {
    this.tenantName = data.tenantName || '';
    this.name = data.name || '';
    this.tenantUniqueName = data.tenantUniqueName || null;
    this.workspaceId = data.workspaceId || null;
    this.tenantId = data.tenantId || null;
    this.status = data.status || 0;
    this.state = data.state || ObjectStateConstants.NONE;
    this.inserted = data.inserted || false;
    this.version = data.version || null;
    this.created = data.created || null;
    this.createdBy = data.createdBy || null;
    this.modified = data.modified || null;
    this.modifiedBy = data.modifiedBy || null;
    this.destinationHashCode = data.destinationHashCode;
    this.readDestinationConnections(data.destinationConnections);
    this.readSourceConnections(sourceConnections);

    this.dataMapping = map(
      function(mappingItem) {
        if (mappingItem.toObject && mappingItem.toObject.indexOf(constants.TENANT_TOKEN) > -1) {
          mappingItem.toObject = mappingItem.toObject.replace(constants.TENANT_TOKEN, this.tenantUniqueName);
        }
        if (mappingItem.toObject && mappingItem.toObject.indexOf(constants.TENANT_NAME_TOKEN) > -1) {
          mappingItem.toObject = mappingItem.toObject.replace(constants.TENANT_NAME_TOKEN, this.name);
        }

        return mappingItem;
      }.bind(this),
      tenantMapping
    );

    this.databaseSources = map(function(database) {
      database.fullDBName = String.format('[{0}] {1}', database.serverTypeName, database.databaseName);
      database.additionalData = {
        fullDBName: database.fullDBName,
        databaseServer: database.databaseServer, 
        databaseUser: database.databaseUser
      };
      database.fullDatabaseKey = String.format(
        '[{0}] {1} - {2} - {3}', 
        database.serverTypeName, 
        database.databaseName, 
        database.databaseServer, 
        database.databaseUser
      );
      return database;
    }, databaseSources);
  });

  /**
   * Populate Workspace tenant data
   * @param {type} data
   * @param {Boolean} isDetail - get tenant detail
   */
  WorkspaceTenant.prototype.poppulateDataAfterValidate = m.action(function(data, isDetail) {
    this.status = data.status || 0;
    this.destinationHashCode = data.destinationHashCode;
    !!isDetail && this.readSourceConnections(data.sourceConnections);
    !!isDetail && this.readDestinationConnections(data.destinationConnections);
    this.markDirty();
  });

  /**
   * Read data sources (mostly this data comes from API)
   * @param  {Object} dbSource
   */
  WorkspaceTenant.prototype.readSourceConnections = m.action(function(sourceConnections) {
    forEach(function(connection) {
      // update value for copy report to show/hide connection which has query-object link to copied reports
      !has('belongToCopiedReport', connection) && CopyManagementUtils.updateDataSoucreBasedOnLinkedReport(connection);
    }, sourceConnections);

    this.sourceConnections = sourceConnections;
    this.querySourceTree.setQuerySources(sourceConnections);
  });

  /**
   * Read destinationConnections (mostly this data comes from API)
   * @param  {Object} destinationConnections
   */
  WorkspaceTenant.prototype.readDestinationConnections = m.action(function(destinationConnections) {
    this.destinationConnections = destinationConnections;
    this.destinationQuerySourceTree.setQuerySources(destinationConnections);
  });

  /**
   * Set property value
   * @param {type} property
   * @param {type} value
   */
  WorkspaceTenant.prototype.setPropertyValue = function(property, value) {
    this[property] = value;
    this.setDirty();
  };

  /**
   * Add mapping
   */
  WorkspaceTenant.prototype.addMapping = function() {
    var newMappingItem = new WorkspaceMapping({ workspaceId: this.workspaceId });

    this.dataMapping.push(newMappingItem);
    this.setDirty();
  };

  /**
   * get mapping list for this tenant
   * @param {array} workspaceMappings
   * @param {array} mappings
   */
  WorkspaceTenant.prototype.getMappingsForTenant = function(workspaceMappings) {
    return workspaceMappings.filter(
      function(mapping) {
        return !!find({ tenantId: this.tenantId }, mapping.workspaceMappingTenants);
      }.bind(this)
    );
  };

  /**
   * Set dirty
   */
  WorkspaceTenant.prototype.setDirty = m.action(function() {
    this.markDirty();
    this.status = 0;
  });

  module.exports = WorkspaceTenant;
})();



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.WorkspaceTenant.js